import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Shirt, Droplets, Sun, Moon, Heart, BookOpen } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta } from "../components/shared/SEOHelper";

const YogaRetreatPackingList = () => {
  usePageMeta({
    title: "Yoga Retreat Packing List | What to Bring to a Yoga Retreat",
    description: "Complete yoga retreat packing list: what to wear, bring, and pack for your UK yoga retreat. Essential items, clothing guide, and insider tips for day",
    canonical: "https://annelielily.com/retreats/packing-list"
  });

  const essentialClothing = [
    "Comfortable yoga leggings or loose trousers (2-3 pairs)",
    "Breathable tops/t-shirts (3-4)",
    "Sports bra or comfortable underwear",
    "Warm layers — cardigan, hoodie, or fleece",
    "Cosy socks or yoga socks with grips",
    "Casual clothes for mealtimes and free time",
    "Sleepwear/pyjamas",
    "Swimwear (if sauna, hot tub, or swimming is included)"
  ];

  const practiceItems = [
    { item: "Yoga mat", note: "Usually provided, but bring yours if you prefer" },
    { item: "Water bottle", note: "Refillable — staying hydrated is essential" },
    { item: "Small towel", note: "For hot yoga or sauna sessions" },
    { item: "Eye pillow or mask", note: "For yoga nidra and relaxation (often provided)" },
    { item: "Journal and pen", note: "For reflections, intentions, and insights" },
    { item: "Light blanket or shawl", note: "For meditation and relaxation" }
  ];

  const personalEssentials = [
    "Toiletries (eco-friendly options appreciated)",
    "Any medications you take",
    "Sunscreen and lip balm",
    "Insect repellent (for outdoor retreats)",
    "Hair ties and headband",
    "Comfortable walking shoes or trainers",
    "Flip flops or slippers for indoors",
    "Reusable shopping bag",
    "Phone charger",
    "Book or e-reader for downtime"
  ];

  const optionalNiceToHave = [
    "Meditation cushion (if you have a favourite)",
    "Essential oils (lavender for relaxation)",
    "Crystals or meaningful objects",
    "Herbal tea bags (if you have preferences)",
    "Earplugs and eye mask for sleeping",
    "Hot water bottle (UK can be chilly!)",
    "Camera for capturing memories",
    "Headphones for travel"
  ];

  const whatNotToBring = [
    "Work laptop or excessive devices",
    "Too many outfit options",
    "Heavy books (one light read is enough)",
    "Alcohol (most retreats are alcohol-free)",
    "Negative energy or expectations!"
  ];

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <PageBreadcrumb currentPage="Packing List" currentSlug="retreats/packing-list" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
              Yoga Retreat Packing List
            </h1>
            <p className="text-neutral-700 max-w-2xl mx-auto leading-relaxed">
              Everything you need to pack for your yoga retreat — from essential clothing 
              to helpful extras. Whether it's a day retreat or week-long yoga holiday, 
              this guide has you covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick Tips */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gold/5 border border-gold/20 p-6 rounded-2xl">
            <h2 className="font-heading text-xl text-neutral-800 mb-3">Quick Packing Tips</h2>
            <div className="grid sm:grid-cols-3 gap-4 text-sm text-neutral-700">
              <div className="flex items-start gap-2">
                <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                <span><strong>Pack light</strong> — you won't need much</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                <span><strong>Layer up</strong> — UK venues can vary in temperature</span>
              </div>
              <div className="flex items-start gap-2">
                <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                <span><strong>Comfort over style</strong> — this isn't a fashion show</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-6 md:px-12 bg-cream">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            
            {/* Clothing */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white/80 p-6 rounded-2xl border border-gold/10"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
                  <Shirt className="w-5 h-5 text-gold" />
                </div>
                <h2 className="font-heading text-xl text-neutral-800">Clothing Essentials</h2>
              </div>
              <ul className="space-y-2">
                {essentialClothing.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-neutral-700 text-sm">
                    <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-neutral-500 text-xs mt-4 italic">
                Tip: Choose breathable, stretchy fabrics. Avoid anything restrictive.
              </p>
            </motion.div>

            {/* Practice Items */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white/80 p-6 rounded-2xl border border-gold/10"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
                  <Sun className="w-5 h-5 text-gold" />
                </div>
                <h2 className="font-heading text-xl text-neutral-800">For Your Practice</h2>
              </div>
              <ul className="space-y-3">
                {practiceItems.map((item, i) => (
                  <li key={i} className="text-sm">
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-neutral-800 font-medium">{item.item}</span>
                        <span className="text-neutral-500 block text-xs">{item.note}</span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Personal Essentials */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white/80 p-6 rounded-2xl border border-gold/10"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
                  <Heart className="w-5 h-5 text-gold" />
                </div>
                <h2 className="font-heading text-xl text-neutral-800">Personal Essentials</h2>
              </div>
              <ul className="space-y-2">
                {personalEssentials.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-neutral-700 text-sm">
                    <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Nice to Have */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white/80 p-6 rounded-2xl border border-gold/10"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center">
                  <Moon className="w-5 h-5 text-gold" />
                </div>
                <h2 className="font-heading text-xl text-neutral-800">Nice to Have</h2>
              </div>
              <ul className="space-y-2">
                {optionalNiceToHave.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-neutral-700 text-sm">
                    <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-neutral-500 text-xs mt-4 italic">
                These are optional extras — don't feel you need them all!
              </p>
            </motion.div>
          </div>

          {/* What NOT to Bring */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 bg-neutral-800 text-white p-6 rounded-2xl"
          >
            <h2 className="font-heading text-xl mb-4">What NOT to Bring</h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {whatNotToBring.map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-neutral-300">
                  <span className="text-gold">✗</span>
                  {item}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Day Retreat vs Residential */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 grid md:grid-cols-2 gap-6"
          >
            <div className="bg-muted p-6 rounded-2xl">
              <h3 className="font-heading text-lg text-neutral-800 mb-3">Day Retreat Packing</h3>
              <p className="text-neutral-600 text-sm mb-3">
                For a day retreat, you need much less:
              </p>
              <ul className="space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  Yoga clothes (wear or bring to change into)
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  Swimwear if sauna included
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  Water bottle
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  Small towel
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  Journal if desired
                </li>
              </ul>
            </div>

            <div className="bg-muted p-6 rounded-2xl">
              <h3 className="font-heading text-lg text-neutral-800 mb-3">International Retreat Additions</h3>
              <p className="text-neutral-600 text-sm mb-3">
                For yoga holidays abroad, also consider:
              </p>
              <ul className="space-y-2 text-sm text-neutral-700">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  Passport and travel documents
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  Travel insurance details
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  Travel adapter
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  Local currency or card
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  Climate-appropriate clothing
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Final Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 text-center"
          >
            <p className="text-neutral-600 italic">
              Remember: you'll receive a specific packing list before each retreat with 
              any venue-specific recommendations. When in doubt, pack less — you're there 
              to relax, not to unpack!
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Book Your Retreat?
            </h2>
            <p className="text-neutral-600 mb-6">
              Explore our upcoming yoga retreats and start planning your wellness escape.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/retreats/yoga-retreats-uk" className="btn-primary">
                View Yoga Retreats UK <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <Link to="/retreats/what-to-expect" className="btn-secondary">
                What to Expect
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <h3 className="font-heading text-lg text-neutral-800 mb-4 text-center">Related Guides</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/retreats/what-to-expect" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">What to Expect</span>
              <p className="text-neutral-500 text-xs mt-1">First retreat guide</p>
            </Link>
            <Link to="/retreats/benefits" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Retreat Benefits</span>
              <p className="text-neutral-500 text-xs mt-1">Why go on retreat</p>
            </Link>
            <Link to="/retreats/beginners" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Beginner's Guide</span>
              <p className="text-neutral-500 text-xs mt-1">New to yoga?</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YogaRetreatPackingList;
